import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import API_URL from "../../config/apiConfig";
import Navbar from "../../components/Navbar";
import Footer from "../home/sections/Footer";
import useJsonLd from "../../seo/useJsonLd";
import { buildRestaurantSchema } from "../../seo/schemaBuilders";
// Swiper
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Thumbs } from "swiper/modules";

const RestaurantDetails = ({ isDashboardView = false }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [restaurant, setRestaurant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [thumbsSwiper, setThumbsSwiper] = useState(null);

  useEffect(() => {
    const fetchRestaurant = async () => {
      try {
        const res = await fetch(`${API_URL}/api/restaurants/${id}`);
        if (!res.ok) throw new Error("Failed to fetch restaurant");
        const data = await res.json();
        setRestaurant(data);
      } catch (err) {
        console.error(err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchRestaurant();
  }, [id]);

  // Only surface Restaurant structured data on the real public page, and
  // only once this restaurant is confirmed publicly published — never on a
  // dashboard/private preview of this same component.
  const restaurantSchema = useMemo(
    () => (!isDashboardView && restaurant?.isPubliclyPublished ? buildRestaurantSchema(restaurant) : null),
    [isDashboardView, restaurant]
  );
  useJsonLd("restaurant", restaurantSchema);

  const handleReservation = () => {
    navigate(`/p/reserve/${id}`);
  };

  if (loading) return <p className="p-4">در حال بارگذاری...</p>;
  if (error) return <p className="p-4 text-red-500">خطا: {error}</p>;
  if (!restaurant) return <p className="p-4">رستوران یافت نشد</p>;

  const gallery = restaurant?.restaurantSettings?.gallery || [];
  const restaurantName = restaurant?.name || "";

  return (
    <>
      {!isDashboardView && <Navbar />}
      <div className="bg-gray-50 min-h-screen">
        <div className="max-w-6xl mx-auto px-4 pt-24 pb-28">
          <div className="grid md:grid-cols-2 gap-14 items-start">

            {/* گالری */}
            <div className="space-y-6">
              {gallery.length > 0 && (
                <>
                  <Swiper
                    modules={[Navigation, Pagination, Thumbs]}
                    navigation
                    pagination={{ clickable: true }}
                    thumbs={thumbsSwiper ? { swiper: thumbsSwiper } : undefined}
                    loop
                    className="w-full aspect-[4/3] rounded-2xl overflow-hidden"
                  >
                    {gallery.map((img, index) => (
                      <SwiperSlide key={index} className="flex items-center justify-center bg-black">
                        <img
                          src={`${API_URL}/uploads/restaurants/${restaurant._id}/${img}`}
                          alt={`${restaurantName} ${index + 1}`}
                          className="max-h-full max-w-full object-contain"
                        />
                      </SwiperSlide>
                    ))}
                  </Swiper>

                  <Swiper
                    onSwiper={setThumbsSwiper}
                    modules={[Thumbs]}
                    spaceBetween={12}
                    slidesPerView={Math.min(gallery.length, 5)}
                    watchSlidesProgress
                    className="h-20"
                  >
                    {gallery.map((img, index) => (
                      <SwiperSlide key={index} className="cursor-pointer">
                        <img
                          src={`${API_URL}/uploads/restaurants/${restaurant._id}/${img}`}
                          alt={`Thumb ${restaurantName} ${index + 1}`}
                          className="w-full h-20 object-cover rounded-xl opacity-70 hover:opacity-100 transition"
                        />
                      </SwiperSlide>
                    ))}
                  </Swiper>
                </>
              )}
            </div>

            {/* اطلاعات */}
            <div className="flex flex-col gap-8">

              {/* عنوان */}
              <div>
                <h1 className="text-4xl font-bold text-gray-900">
                  {restaurantName}
                </h1>
                <p className="text-gray-500 mt-2 text-sm">
                  {restaurant.phone || "شماره تماس ثبت نشده"}
                </p>
              </div>
              <div className="flex gap-3 mt-4 flex-wrap">
                <span className="text-xs px-3 py-1 bg-primary/10 text-primary rounded-full">
                  مناسب تولد
                </span>
              </div>
              {/* آدرس و توضیح */}
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <div>
                  <p className="text-xs uppercase tracking-wider text-gray-400 mb-1">
                    آدرس
                  </p>
                  <p>{restaurant.restaurantSettings?.address || "نامشخص"}</p>
                </div>

                <div>
                  <p className="text-xs uppercase tracking-wider text-gray-400 mb-1">
                    ساعت کاری
                  </p>
                  <p>{restaurant.restaurantSettings?.openingHours || "نامشخص"}</p>
                </div>

                {restaurant.restaurantSettings?.description && (
                  <div>
                    <p className="text-xs uppercase tracking-wider text-gray-400 mb-1">
                      توضیحات
                    </p>
                    <p className="text-gray-600">
                      {restaurant.restaurantSettings.description}
                    </p>
                  </div>
                )}
              </div>

              {/* اطلاعات رزرو */}
              <div className="border-t border-gray-200 pt-6 space-y-3">
                <div className="flex justify-between text-gray-700">
                  <span>تعداد میز</span>
                  <span className="font-medium">
                    {restaurant.restaurantSettings?.tableCount || 0}
                  </span>
                </div>

                <div className="flex justify-between text-gray-900">
                  <span>هزینه رزرو میز</span>
                  <span className="font-semibold text-primary">
                    {restaurant.restaurantSettings?.tableReservePrice
                      ? `${restaurant.restaurantSettings.tableReservePrice} تومان`
                      : "نامشخص"}
                  </span>
                </div>
              </div>

              {/* دکمه‌ها */}
              <div className="flex flex-col gap-4 pt-4">
                <button
                  onClick={handleReservation}
                  className="w-full py-3 rounded-xl bg-primary text-white font-medium
                     hover:bg-primary-dark transition-all duration-300"
                >
                  رزرو میز
                </button>

                {restaurant.restaurantSettings?.showMenuButton && (
                  <button
                    onClick={() => navigate(`/menu/${restaurant.menuLink}`)}
                    className="w-full py-3 rounded-xl border border-gray-300
                       text-gray-800 font-medium
                       hover:border-primary hover:text-primary
                       transition-all duration-300"
                  >
                    مشاهده منو
                  </button>
                )}
              </div>

            </div>
          </div>
        </div>
      </div>

      {/* شبکه‌های اجتماعی و نقشه */}
      <div className="border-t border-gray-200 pt-8 space-y-6">

        {/* شبکه‌های اجتماعی */}
        <div className="space-y-3">
          <p className="text-xs uppercase tracking-wider text-gray-400">
            شبکه‌های اجتماعی
          </p>

          <div className="flex gap-4 flex-wrap">
            {restaurant.restaurantSettings?.instagram && (
              <a
                href={restaurant.restaurantSettings.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-gray-700 hover:text-primary transition"
              >
                اینستاگرام
              </a>
            )}

            {restaurant.restaurantSettings?.telegram && (
              <a
                href={restaurant.restaurantSettings.telegram}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-gray-700 hover:text-primary transition"
              >
                تلگرام
              </a>
            )}

            {restaurant.restaurantSettings?.website && (
              <a
                href={restaurant.restaurantSettings.website}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-gray-700 hover:text-primary transition"
              >
                وبسایت
              </a>
            )}
          </div>
        </div>

        {/* نقشه */}
        {restaurant.restaurantSettings?.mapLink && (
          <div className="space-y-3">
            <p className="text-xs uppercase tracking-wider text-gray-400">
              موقعیت روی نقشه
            </p>

            <div className="w-full h-64 rounded-2xl overflow-hidden border border-gray-200">
              <iframe
                title="موقعیت رستوران روی نقشه"
                src={restaurant.restaurantSettings.mapLink}
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              ></iframe>
            </div>
          </div>
        )}

      </div>
      {!isDashboardView && <Footer />}
    </>


  );
};

export default RestaurantDetails;
